import { useTranslation } from 'react-i18next';
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
  Radio,
  List,
  ListItem,
  ListItemText,
  useMediaQuery
} from '@material-ui/core';
import { MySlider } from '../FormWidgets/MySlider';
import { MyTimePicker } from '../FormWidgets/DatePicker';
import Alert from '@material-ui/lab/Alert';
import { InfoButton } from '../FormWidgets/Description';
import useLightStyles from './lightChannelStyles';
import {
  defaultLightProfile,
  editProfilePoint,
  clockOffsetForPoint,
  nextProfilePoint,
  formatLightMinute,
  profileError,
  resolvePoint,
  sortedPoints
} from '../Utils/light_profiles';

// Keep intermediate signed input (including a lone minus) local until committed.
function PointNumberInput({
  value,
  onCommit,
  live = false,
  min = -Infinity,
  max = Infinity,
  ...props
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText(String(value));
  }, [value]);
  const valid = (text) =>
    /^-?\d+$/.test(text.trim()) &&
    Number.isSafeInteger(Number(text)) &&
    Number(text) >= min &&
    Number(text) <= max;
  const commit = () => {
    if (valid(text)) {
      const accepted = onCommit(Number(text));
      setText(String(accepted ?? value));
    } else setText(String(value));
  };
  return (
    <TextField
      {...props}
      value={text}
      onChange={(event) => {
        const next = event.target.value;
        setText(next);
        if (live && valid(next)) {
          const accepted = onCommit(Number(next));
          if (accepted !== undefined && accepted !== Number(next)) setText(String(accepted));
        }
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          commit();
        }
      }}
    />
  );
}

// A point's stable editor identity follows it when dragging changes sort order.
// These UI identities are stripped by the submission adapter.
let nextPointId = 0;
const withIds = (points, ownedIds) => {
  const seen = new Set();
  return points.map((point) => {
    const id =
      ownedIds.has(point._id) && !seen.has(point._id) ? point._id : `light-point-${++nextPointId}`;
    seen.add(id);
    return { ...point, _id: id };
  });
};
export default function LightCurveEditor({
  points = [],
  cycle,
  onChange,
  disabled = false,
  availablePoints = 256,
  emptyPreview
}) {
  const { t } = useTranslation();
  const classes = useLightStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedPoint, setSelectedPoint] = useState(null);
  const profileColour = theme.palette.primary.main;
  const [message, setMessage] = useState('');
  const [viewport, setViewport] = useState(null);
  const drag = useRef(null);
  const current = useRef(null);
  const identified = useMemo(() => {
    // Readback replaces points without editor IDs. Keep identities only for
    // the current profile, while local edits retain them across reordering.
    return withIds(points, new Set((current.current ?? []).map((point) => point._id)));
  }, [points]);
  current.current = identified;
  const resolved = sortedPoints(identified, cycle);
  const activePoint = resolved.find((point) => point._id === selectedPoint) ?? resolved[0];
  const activeId = activePoint?._id;
  const chartWidth = mobile ? 480 : 800;
  const left = viewport?.left ?? Math.min(cycle.start - 60, ...resolved.map((p) => p.minute));
  const right = viewport?.right ?? Math.max(cycle.end + 60, ...resolved.map((p) => p.minute));
  const x = (minute) => 40 + ((minute - left) / (right - left)) * (chartWidth - 80);
  const y = (level) => 180 - level * 1.5;
  const error = profileError(points, cycle);
  const change = (id, fields) => {
    const index = current.current.findIndex((p) => p._id === id);
    if (index < 0) return;
    const result = editProfilePoint(current.current, index, fields, cycle);
    setMessage(
      result.error ||
        (result.snapped
          ? t('lighting_point_moved', { time: formatLightMinute(result.minute) })
          : '')
    );
    if (!result.error) {
      current.current = result.points;
      onChange(result.points);
      return result.point;
    }
    return undefined;
  };
  const changeAnchor = (point, anchor) => {
    const offset_minutes = resolvePoint(point, cycle) - (anchor === 1 ? cycle.end : cycle.start);
    if (Math.abs(offset_minutes) > 1440) {
      setMessage(t('lighting_anchor_out_of_range'));
      return;
    }
    const next = current.current.map((p) =>
      p._id === point._id ? { ...p, anchor, offset_minutes } : p
    );
    const error = profileError(next, cycle);
    if (error) {
      setMessage(error);
      return;
    }
    setMessage('');
    current.current = next;
    onChange(next);
  };
  const pointClock = (point, index) => {
    const time = formatLightMinute(resolvePoint(point, cycle));
    return (
      <MyTimePicker
        id={`${point._id}-clock`}
        value={`${time.slice(0, 5)}:00`}
        label={time}
        disabled={disabled}
        style={{ width: 120, flexShrink: 0, marginTop: 0 }}
        InputProps={{ style: { fontSize: 18, fontWeight: 600, color: theme.palette.primary.main } }}
        inputProps={{ 'aria-label': t('lighting_point_clock', { number: index + 1, time }) }}
        onChange={(value) => {
          if (!value || Number.isNaN(value.getTime())) return;
          change(point._id, {
            offset_minutes: clockOffsetForPoint(
              point,
              cycle,
              value.getHours() * 60 + value.getMinutes()
            )
          });
        }}
      />
    );
  };
  const move = (event) => {
    if (!drag.current || disabled) return;
    const svg = event.currentTarget;
    const matrix = svg.getScreenCTM?.();
    let position;
    if (matrix && svg.createSVGPoint) {
      const screenPoint = svg.createSVGPoint();
      screenPoint.x = event.clientX;
      screenPoint.y = event.clientY;
      position = screenPoint.matrixTransform(matrix.inverse());
    } else {
      const rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      position = {
        x: ((event.clientX - rect.left) / rect.width) * chartWidth,
        y: ((event.clientY - rect.top) / rect.height) * 230
      };
    }
    const point = current.current.find((p) => p._id === drag.current);
    if (!point) return;
    const minute = Math.round(left + ((position.x - 40) / (chartWidth - 80)) * (right - left));
    const level = Math.round((180 - position.y) / 1.5);
    change(point._id, {
      offset_minutes: minute - (point.anchor === 1 ? cycle.end : cycle.start),
      level
    });
  };
  let path = '';
  resolved.forEach((point, i) => {
    if (!i) path = `M${x(point.minute)},${y(point.level)}`;
    else {
      const previous = resolved[i - 1];
      if (previous.interpolation === 1) path += ` H${x(point.minute)} V${y(point.level)}`;
      else path += ` L${x(point.minute)},${y(point.level)}`;
    }
  });
  const add = () => {
    if (!points.length) {
      if (availablePoints < 2) {
        setMessage(t('lighting_profile_needs_points'));
        return;
      }
      current.current = withIds(defaultLightProfile(), new Set());
      onChange(current.current);
      return;
    }
    const added = withIds([nextProfilePoint(identified, activePoint)], new Set())[0];
    const next = [...identified, added];
    const result = editProfilePoint(next, next.length - 1, {}, cycle, {
      allowPartialRepair: false
    });
    setMessage(
      result.error ||
        (result.snapped
          ? t('lighting_point_added', { time: formatLightMinute(result.minute) })
          : '')
    );
    if (!result.error) {
      setSelectedPoint(added._id);
      current.current = result.points;
      onChange(result.points);
    }
  };
  return (
    <Box my={2}>
      <Box className={classes.fields}>
        <Typography variant="body2">
          {t('lighting_day', {
            defaultValue: 'Day:'
          })}
          {formatLightMinute(cycle.start)} {'\u2013'} {formatLightMinute(cycle.end)}{' '}
          <InfoButton brief={t('lighting_curve_help_title')} content={t('lighting_curve_help')} />
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="info">{message}</Alert>}
      {points.length === 0 && emptyPreview}
      {mobile && resolved.length > 0 && (
        <List dense aria-label={t('lighting_point_list')} className={classes.pointList}>
          {resolved.map((point, index) => (
            <ListItem
              button
              key={point._id}
              selected={activeId === point._id}
              onClick={() => setSelectedPoint(point._id)}
            >
              <Radio
                checked={activeId === point._id}
                color="secondary"
                inputProps={{ 'aria-label': t('lighting_select_point', { number: index + 1 }) }}
                onChange={() => setSelectedPoint(point._id)}
              />
              <ListItemText
                primary={`${index + 1}. ${formatLightMinute(point.minute)} / ${point.level}%`}
                secondary={`${t(point.anchor === 1 ? 'lighting_day_end' : 'lighting_day_start')} ${point.offset_minutes >= 0 ? '+' : ''}${point.offset_minutes} min`}
              />
            </ListItem>
          ))}
        </List>
      )}
      {points.length > 0 && (
        <svg
          viewBox={`0 0 ${chartWidth} 230`}
          aria-label={t('lighting_profile_chart')}
          role="img"
          style={{
            width: '100%',
            maxHeight: 320,
            touchAction: 'none'
          }}
          onPointerMove={move}
          onPointerUp={() => {
            drag.current = null;
            setViewport(null);
          }}
          onPointerCancel={() => {
            drag.current = null;
            setViewport(null);
          }}
        >
          <rect
            x={x(cycle.start)}
            y="25"
            width={x(cycle.end) - x(cycle.start)}
            height="155"
            fill="currentColor"
            opacity="0.06"
          />
          {[0, 50, 100].map((level) => (
            <g key={level}>
              <line
                x1="40"
                x2={chartWidth - 40}
                y1={y(level)}
                y2={y(level)}
                stroke="currentColor"
                opacity="0.15"
              />
              <text x="0" y={y(level) + 4} fill="currentColor" fontSize="13">
                {level}%
              </text>
            </g>
          ))}
          <path d={path} stroke={profileColour} strokeWidth="3" fill="none" />
          {resolved.map((point) => (
            <g key={point._id}>
              {mobile && (
                <circle
                  cx={x(point.minute)}
                  cy={y(point.level)}
                  r={28}
                  fill="transparent"
                  onClick={() => setSelectedPoint(point._id)}
                  onPointerDown={(event) => {
                    if (disabled) return;
                    setSelectedPoint(point._id);
                    drag.current = point._id;
                    setViewport({ left, right });
                    current.current = identified;
                    event.currentTarget.ownerSVGElement.setPointerCapture?.(event.pointerId);
                  }}
                />
              )}
              <circle
                cx={x(point.minute)}
                cy={y(point.level)}
                r={activeId === point._id ? (mobile ? 14 : 11) : mobile ? 11 : 8}
                fill={profileColour}
                stroke={activeId === point._id ? theme.palette.secondary.main : 'currentColor'}
                strokeWidth={activeId === point._id ? 4 : 1}
                onFocus={() => setSelectedPoint(point._id)}
                onClick={() => setSelectedPoint(point._id)}
                tabIndex={disabled ? -1 : 0}
                role="slider"
                aria-label={t('lighting_point_time', { time: formatLightMinute(point.minute) })}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={point.level}
                onKeyDown={(event) => {
                  if (disabled) return;
                  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
                    event.preventDefault();
                    change(
                      point._id,
                      event.key === 'ArrowLeft' || event.key === 'ArrowRight'
                        ? {
                            offset_minutes:
                              point.offset_minutes + (event.key === 'ArrowRight' ? 1 : -1)
                          }
                        : {
                            level: point.level + (event.key === 'ArrowUp' ? 1 : -1)
                          }
                    );
                  }
                }}
                onPointerDown={(event) => {
                  if (disabled) return;
                  setSelectedPoint(point._id);
                  drag.current = point._id;
                  setViewport({ left, right });
                  current.current = identified;
                  event.currentTarget.ownerSVGElement.setPointerCapture?.(event.pointerId);
                }}
              />
            </g>
          ))}
          <text x="40" y="215" fill="currentColor" fontSize="13">
            {formatLightMinute(left)}
          </text>
          <text x={chartWidth - 40} y="215" textAnchor="end" fill="currentColor" fontSize="13">
            {formatLightMinute(right)}
          </text>
        </svg>
      )}
      {resolved.map(
        (point, index) =>
          (!mobile || activeId === point._id) && (
            <Box
              key={point._id}
              className={`${classes.point} ${activeId === point._id ? classes.selectedPoint : ''}`}
              data-selected={activeId === point._id ? 'true' : undefined}
              onFocus={() => setSelectedPoint(point._id)}
              onClick={() => setSelectedPoint(point._id)}
            >
              {!mobile && (
                <Radio
                  color="secondary"
                  checked={activeId === point._id}
                  inputProps={{ 'aria-label': t('lighting_select_point', { number: index + 1 }) }}
                  onChange={() => setSelectedPoint(point._id)}
                />
              )}
              <TextField
                fullWidth
                id={`${point._id}-anchor`}
                select
                label={t('lighting_point_anchor', { number: index + 1 })}
                value={point.anchor}
                disabled={disabled}
                onChange={(e) => changeAnchor(point, Number(e.target.value))}
              >
                <MenuItem value={0}>
                  {t('lighting_day_start', {
                    defaultValue: 'Day start'
                  })}
                </MenuItem>
                <MenuItem value={1}>
                  {t('lighting_day_end', {
                    defaultValue: 'Day end'
                  })}
                </MenuItem>
              </TextField>
              {mobile ? (
                <>
                  <Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <PointNumberInput
                        fullWidth
                        live
                        min={-1440}
                        max={1440}
                        id={`${point._id}-offset`}
                        label={t('lighting_point_offset', { number: index + 1 })}
                        value={point.offset_minutes}
                        inputProps={{ inputMode: 'numeric' }}
                        disabled={disabled}
                        onCommit={(offset_minutes) =>
                          change(point._id, { offset_minutes })?.offset_minutes
                        }
                      />
                      {pointClock(point, index)}
                    </Box>
                    <TextField
                      select
                      fullWidth
                      id={`${point._id}-direction`}
                      label={t('lighting_offset_direction')}
                      value={point.offset_minutes < 0 ? -1 : 1}
                      disabled={disabled}
                      onChange={(event) =>
                        change(point._id, {
                          offset_minutes:
                            Number(event.target.value) * Math.abs(point.offset_minutes)
                        })
                      }
                    >
                      <MenuItem value={-1}>{t('lighting_before_anchor')}</MenuItem>
                      <MenuItem value={1}>{t('lighting_after_anchor')}</MenuItem>
                    </TextField>
                    <MySlider
                      id={`${point._id}-offset-control`}
                      min={-1440}
                      max={1440}
                      unit=""
                      value={point.offset_minutes}
                      disabled={disabled}
                      color="primary"
                      aria-label={t('lighting_point_offset', { number: index + 1 })}
                      DecClick={() =>
                        change(point._id, { offset_minutes: point.offset_minutes - 1 })
                      }
                      IncClick={() =>
                        change(point._id, { offset_minutes: point.offset_minutes + 1 })
                      }
                      onChange={(_, value) => change(point._id, { offset_minutes: value })}
                    />
                  </Box>
                  <Box>
                    <PointNumberInput
                      fullWidth
                      id={`${point._id}-level`}
                      label={t('lighting_point_level', { number: index + 1 })}
                      value={point.level}
                      inputProps={{ inputMode: 'numeric' }}
                      disabled={disabled}
                      onCommit={(level) => change(point._id, { level })?.level}
                    />
                    <MySlider
                      id={`${point._id}-level-control`}
                      min={0}
                      max={100}
                      unit="%"
                      value={point.level}
                      disabled={disabled}
                      color="primary"
                      aria-label={t('lighting_point_level', { number: index + 1 })}
                      DecClick={() => change(point._id, { level: point.level - 1 })}
                      IncClick={() => change(point._id, { level: point.level + 1 })}
                      onChange={(_, value) => change(point._id, { level: value })}
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
                    <PointNumberInput
                      fullWidth
                      live
                      min={-1440}
                      max={1440}
                      id={`${point._id}-offset`}
                      label={t('lighting_point_offset', { number: index + 1 })}
                      value={point.offset_minutes}
                      inputProps={{ inputMode: 'numeric' }}
                      disabled={disabled}
                      onCommit={(offset_minutes) =>
                        change(point._id, { offset_minutes })?.offset_minutes
                      }
                    />
                    {pointClock(point, index)}
                  </Box>
                  <TextField
                    fullWidth
                    id={`${point._id}-level`}
                    type="number"
                    label={t('lighting_point_level', { number: index + 1 })}
                    value={point.level}
                    inputProps={{
                      min: 0,
                      max: 100
                    }}
                    disabled={disabled}
                    onChange={(e) =>
                      change(point._id, {
                        level: Number(e.target.value)
                      })
                    }
                  />
                </>
              )}
              <TextField
                fullWidth
                id={`${point._id}-transition`}
                select
                label={t('lighting_point_transition', { number: index + 1 })}
                value={point.interpolation}
                disabled={disabled}
                onChange={(e) =>
                  change(point._id, {
                    interpolation: Number(e.target.value)
                  })
                }
              >
                <MenuItem value={0}>
                  {t('lighting_linear', {
                    defaultValue: 'Linear to next'
                  })}
                </MenuItem>
                <MenuItem value={1}>
                  {t('lighting_hold', {
                    defaultValue: 'Hold until next'
                  })}
                </MenuItem>
              </TextField>
              <Button
                disabled={disabled || points.length <= 2}
                onClick={() => onChange(identified.filter((p) => p._id !== point._id))}
              >
                {t('lighting_remove_point', {
                  defaultValue: 'Remove point'
                })}
                {index + 1}
              </Button>
            </Box>
          )
      )}
      <Box className={classes.actions}>
        <Button
          color="primary"
          variant="outlined"
          disabled={disabled || availablePoints < (points.length ? 1 : 2)}
          onClick={add}
        >
          {t(points.length ? 'lighting_add_point' : 'lighting_create_profile')}
        </Button>
        {points.length > 0 && (
          <Button disabled={disabled} onClick={() => onChange([])}>
            {t('lighting_default_profile', {
              defaultValue: 'Use default profile'
            })}
          </Button>
        )}
        <Typography variant="caption" color="textSecondary">
          {t('lighting_points_available', { count: availablePoints })}
        </Typography>
        <InfoButton
          brief={t('lighting_default_profile_help')}
          content={t('lighting_default_profile_description')}
        />
      </Box>
    </Box>
  );
}
